/**
 * useAgentChat - Unified React Hook for AG-UI Agent Chat
 *
 * Connects to the agent backend and handles:
 * - SSE streaming connection
 * - AG-UI event parsing and state management
 * - Widget/Artifact collection for canvas
 * - CopilotKit-style agent state for real-time UI feedback
 *
 * Based on patterns from useStatcastAgentChat.js but enhanced for:
 * - Artifact extraction (charts, tables, SQL)
 * - Inline trace steps
 * - Canvas integration
 *
 * @see https://docs.ag-ui.com/introduction
 */

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { inferVisualizationFromQuery } from '../lib/chat/visualizationInference';

// AG-UI Event Types
const EventType = {
  RUN_STARTED: 'run_started',
  RUN_FINISHED: 'run_finished',
  STEP_STARTED: 'step_started',
  STEP_FINISHED: 'step_finished',
  TEXT_MESSAGE_START: 'text_message_start',
  TEXT_MESSAGE_CONTENT: 'text_message_content',
  TEXT_MESSAGE_END: 'text_message_end',
  TOOL_CALL_START: 'tool_call_start',
  TOOL_CALL_ARGS: 'tool_call_args',
  TOOL_CALL_END: 'tool_call_end',
  TOOL_CALL_RESULT: 'tool_call_result',
  AGENT_HANDOFF: 'agent_handoff',
  AGENT_THINKING: 'agent_thinking',
  WIDGET_RENDER: 'widget_render',
  CHART_SPEC: 'chart_spec',
  ORCHESTRATION: 'orchestration',
  ERROR: 'error',
  DONE: 'done',
};

// Artifact types for canvas
const ArtifactType = {
  CHART: 'chart',
  DATA_TABLE: 'data_table',
  SQL_QUERY: 'sql_query',
  STRATEGY: 'strategy',
  METRIC: 'metric',
  IMAGE: 'image',
  SHINY: 'shiny', // R Shiny-style interactive visualizations
  VIDEO_REPORT: 'video_report', // Video analysis report artifact
  MCP_APP: 'mcp_app', // MCP Apps interactive UI resources
};

function isBasketballNl2SqlResponse(data) {
  return Boolean(
    data
    && typeof data === 'object'
    && Object.prototype.hasOwnProperty.call(data, 'sql')
    && Array.isArray(data.results)
  );
}

function formatNl2SqlPreview(rows = []) {
  if (!rows.length) {
    return 'No rows returned.';
  }

  const columns = Object.keys(rows[0] || {});
  if (!columns.length) {
    return 'No rows returned.';
  }

  const header = `| ${columns.join(' | ')} |`;
  const divider = `| ${columns.map(() => '---').join(' | ')} |`;
  const lines = rows.slice(0, 8).map((row) => {
    const values = columns.map((col) => {
      const value = row[col];
      if (value === null || value === undefined) {
        return '-';
      }
      const asString = String(value);
      return asString.length > 42 ? `${asString.slice(0, 39)}...` : asString;
    });
    return `| ${values.join(' | ')} |`;
  });

  return [header, divider, ...lines].join('\n');
}

function buildNl2SqlAssistantMessage(query, data) {
  const rowCount = data.rowCount || data.results?.length || 0;
  const parts = [];

  parts.push(`I ran a BigQuery analysis for: **${query}**`);
  parts.push('');
  if (data.sql) {
    parts.push('**Generated SQL**');
    parts.push('```sql');
    parts.push(data.sql);
    parts.push('```');
    parts.push('');
  }
  parts.push(`**Rows returned:** ${rowCount}`);
  parts.push('');
  parts.push(formatNl2SqlPreview(data.results || []));
  if (rowCount > 8) {
    parts.push('');
    parts.push(`_Showing 8 of ${rowCount} rows._`);
  }

  return parts.join('\n');
}

// Initial agent state
const initialAgentState = {
  status: 'idle', // idle | analyzing | querying | interpreting | strategizing | complete | error
  currentAgent: null,
  currentStep: null,
  query: '',
  intent: null,
  entities: [],
  progress: 0,
  thinking: null,
  dataPreview: null,
  insightPreview: null,
  strategyPreview: null,
};

/**
 * Main hook for agent chat functionality
 */
function useAgentChat({
  endpoint = '/api/agent/chat',
  sport = 'baseball',
  onArtifact = null,
  onTurnComplete = null,
}) {
  // Core state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [textContent, setTextContent] = useState('');

  // Agent state (CopilotKit pattern)
  const [agentState, setAgentState] = useState(initialAgentState);

  // Trace steps for inline display
  const [traceSteps, setTraceSteps] = useState([]);

  // Artifacts for canvas
  const [artifacts, setArtifacts] = useState([]);

  // Widgets for inline rendering
  const [widgets, setWidgets] = useState([]);

  // Refs
  const abortControllerRef = useRef(null);
  const textBufferRef = useRef('');
  const citationsRef = useRef(null);
  const dataFreshnessRef = useRef(null);
  const stepIdCounterRef = useRef(0);
  const guardRefusedRef = useRef(false);

  // Refs for finalization (avoid stale closures in sendMessage)
  const traceStepsRef = useRef([]);
  const widgetsRef = useRef([]);
  const artifactsRef = useRef([]);
  const messagesRef = useRef([]);
  const onTurnCompleteRef = useRef(onTurnComplete);

  // Keep onTurnComplete ref in sync
  useEffect(() => { onTurnCompleteRef.current = onTurnComplete; }, [onTurnComplete]);

  /**
   * Generate unique step ID
   */
  const generateStepId = useCallback(() => {
    stepIdCounterRef.current += 1;
    return `step-${Date.now()}-${stepIdCounterRef.current}`;
  }, []);

  /**
   * Update agent state (merge partial updates)
   */
  const updateAgentState = useCallback((updates) => {
    setAgentState(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * Add a trace step
   */
  const addTraceStep = useCallback((step) => {
    const stepWithId = {
      ...step,
      id: step.id || generateStepId(),
      timestamp: Date.now(),
    };
    setTraceSteps(prev => [...prev, stepWithId]);
    return stepWithId.id;
  }, [generateStepId]);

  /**
   * Add an artifact to the canvas
   */
  const addArtifact = useCallback((artifact) => {
    const artifactWithId = {
      ...artifact,
      id: artifact.id || `artifact-${Date.now()}`,
      timestamp: Date.now(),
      pinned: false,
    };
    setArtifacts(prev => [...prev, artifactWithId]);
    onArtifact?.(artifactWithId);
    return artifactWithId;
  }, [onArtifact]);

  // Keep refs in sync with state for use in async closures
  useEffect(() => { traceStepsRef.current = traceSteps; }, [traceSteps]);
  useEffect(() => { widgetsRef.current = widgets; }, [widgets]);
  useEffect(() => { artifactsRef.current = artifacts; }, [artifacts]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  /**
   * Reset all state for a new conversation
   */
  const reset = useCallback(() => {
    setMessages([]);
    setTextContent('');
    setError(null);
    setAgentState(initialAgentState);
    setTraceSteps([]);
    setArtifacts([]);
    setWidgets([]);
    textBufferRef.current = '';
    citationsRef.current = null;
    dataFreshnessRef.current = null;
    stepIdCounterRef.current = 0;
  }, []);

  /**
   * Clear just the current trace (keep messages)
   */
  const clearTrace = useCallback(() => {
    setTraceSteps([]);
    setWidgets([]);
    setTextContent('');
    textBufferRef.current = '';
    citationsRef.current = null;
    dataFreshnessRef.current = null;
    guardRefusedRef.current = false;
  }, []);

  /**
   * Map agent IDs to status labels
   */
  const getAgentStatus = useCallback((agentId) => {
    const statusMap = {
      'statcast-orchestrator': 'analyzing',
      'statcast-nl2sql': 'querying',
      'statcast-insight': 'interpreting',
      'statcast-strategy': 'strategizing',
      'baseball_analyst': 'analyzing',
      'routing_agent': 'routing',
    };
    return statusMap[agentId] || 'processing';
  }, []);

  /**
   * Calculate progress based on step
   */
  const calculateProgress = useCallback((stepName, agentId) => {
    if (stepName === 'intent_classification') return 10;
    if (stepName === 'routing') return 15;
    if (agentId?.includes('nl2sql') || stepName?.includes('sql')) return 40;
    if (agentId?.includes('insight') || stepName?.includes('insight')) return 70;
    if (agentId?.includes('strategy') || stepName?.includes('strategy')) return 90;
    return 50;
  }, []);

  /**
   * Handle individual AG-UI events
   */
  const handleEvent = useCallback((event) => {
    switch (event.type) {
      case EventType.RUN_STARTED:
        updateAgentState({ status: 'analyzing', progress: 5 });
        addTraceStep({
          type: 'user',
          label: 'Query received',
          text: event.query || agentState.query,
        });
        break;

      case EventType.STEP_STARTED: {
        addTraceStep({
          type: event.stepName?.includes('sql') ? 'sql' :
                event.stepName?.includes('intent') ? 'intent' :
                event.stepName?.includes('model') ? 'model' : 'thinking',
          label: event.stepName?.replace(/_/g, ' ') || 'Processing',
          streaming: true,
        });
        updateAgentState({
          currentStep: event.stepName,
          currentAgent: event.agentId,
          status: getAgentStatus(event.agentId),
          progress: calculateProgress(event.stepName, event.agentId),
        });
        break;
      }

      case EventType.STEP_FINISHED:
        // Find and update the step
        setTraceSteps(prev => {
          const lastMatchingStep = [...prev].reverse().find(
            s => s.label?.toLowerCase().includes(event.stepName?.toLowerCase().replace(/_/g, ' '))
          );
          if (lastMatchingStep) {
            return prev.map(s =>
              s.id === lastMatchingStep.id
                ? { ...s, streaming: false, duration: event.duration }
                : s
            );
          }
          return prev;
        });

        // Update progress
        updateAgentState({
          progress: Math.min(calculateProgress(event.stepName, event.agentId) + 10, 95),
        });

        // Handle intent results
        if (event.stepName === 'intent_classification' && event.result) {
          updateAgentState({
            intent: event.result.intent,
            entities: event.result.entities?.players || [],
          });
        }
        break;

      case EventType.AGENT_HANDOFF:
        addTraceStep({
          type: 'thinking',
          label: `Handoff: ${event.fromAgent} → ${event.toAgent}`,
          thought: event.reason,
        });
        updateAgentState({
          currentAgent: event.toAgent,
          status: getAgentStatus(event.toAgent),
          thinking: `Handing off: ${event.reason}`,
        });
        break;

      case EventType.AGENT_THINKING:
        addTraceStep({
          type: 'reasoning',
          label: 'Agent Thinking',
          thought: event.thought,
        });
        updateAgentState({ thinking: event.thought });
        break;

      case EventType.ORCHESTRATION:
        if (event.applied) {
          addTraceStep({
            type: 'reasoning',
            label: 'Claude orchestration',
            thought: event.summary || event.intent || 'Query orchestration applied',
          });
        }
        break;

      case EventType.TOOL_CALL_START:
        addTraceStep({
          type: 'tool_call',
          label: `Tool: ${event.tool}`,
          input: event.args,
          streaming: true,
        });
        break;

      case EventType.TOOL_CALL_RESULT:
        // Update the tool call step with output
        setTraceSteps(prev => {
          const toolStep = [...prev].reverse().find(s => s.type === 'tool_call' && s.streaming);
          if (toolStep) {
            return prev.map(s =>
              s.id === toolStep.id
                ? { ...s, streaming: false, output: event.result, status: event.success ? 'success' : 'error' }
                : s
            );
          }
          return prev;
        });
        break;

      case EventType.TEXT_MESSAGE_CONTENT:
        textBufferRef.current += event.delta;
        setTextContent(textBufferRef.current);
        break;

      case EventType.TEXT_MESSAGE_END:
        // Finalize text content
        setTextContent(textBufferRef.current);
        break;

      case EventType.WIDGET_RENDER:
        // Add widget for inline rendering
        setWidgets(prev => [...prev, {
          widgetType: event.widgetType,
          widgetId: event.widgetId || `widget-${Date.now()}`,
          data: event.data,
          metadata: event.metadata,
        }]);

        // Extract artifacts for canvas
        if (event.widgetType === 'chart' || event.widgetType === ArtifactType.CHART) {
          addArtifact({
            type: ArtifactType.CHART,
            title: event.data?.title || 'Chart',
            data: event.data,
          });
        } else if (event.widgetType === 'data_table' || event.widgetType === ArtifactType.DATA_TABLE) {
          addArtifact({
            type: ArtifactType.DATA_TABLE,
            title: event.data?.title || 'Query Results',
            data: event.data,
          });
          updateAgentState({
            dataPreview: {
              rowCount: event.data?.rows?.length || 0,
              columns: event.data?.columns?.length || 0,
            },
          });
        } else if (event.widgetType === 'strategy' || event.widgetType === ArtifactType.STRATEGY) {
          addArtifact({
            type: ArtifactType.STRATEGY,
            title: 'Strategy Recommendations',
            data: event.data,
          });
          updateAgentState({
            strategyPreview: {
              recommendationCount: event.data?.recommendations?.length || 0,
            },
          });
        }
        break;

      case 'mcp_app_render':
        addArtifact({
          type: ArtifactType.MCP_APP,
          title: event.title || 'Visualization',
          resourceUri: event.resourceUri,
          toolName: event.toolName,
          toolInput: event.toolInput,
          toolResult: event.toolResult,
        });
        break;

      case EventType.CHART_SPEC:
        addArtifact({
          type: ArtifactType.CHART,
          title: event.title || 'Visualization',
          data: {
            chartType: event.chartType,
            spec: event.spec,
            data: event.data,
          },
        });
        break;

      case EventType.ERROR:
        setError(event.message);
        addTraceStep({
          type: 'error',
          label: 'Error',
          content: event.message,
        });
        updateAgentState({ status: 'error', progress: 0 });
        break;

      case EventType.DONE:
        if (event.citations) {
          citationsRef.current = event.citations;
          dataFreshnessRef.current = event.dataFreshness;
        }
        addTraceStep({
          type: 'results',
          label: 'Complete',
        });
        updateAgentState({
          status: 'complete',
          progress: 100,
          thinking: null,
        });
        break;

      default:
        // Handle custom events (agent_selected, text, content, done, etc.)
        if (event.type === 'guard_refusal') {
          // Post-filter caught a leak in the assistant response — overwrite
          // the accumulated buffer with the refusal so the final message
          // doesn't expose whatever partial content streamed through.
          const refusalText = event.message || 'I can only help with sports questions.';
          textBufferRef.current = refusalText;
          setTextContent(refusalText);
          guardRefusedRef.current = true;
          updateAgentState({ status: 'complete', progress: 100, thinking: null });
        } else if (event.type === 'agent' || event.type === 'agent_selected') {
          updateAgentState({ currentAgent: event.agent || event.agentId });
        } else if (event.type === 'text' || event.type === 'content') {
          // Accumulate text chunks (agentChat.js sends incremental content)
          textBufferRef.current += (event.text || event.content || '');
          setTextContent(textBufferRef.current);
        } else if (event.type === 'complete') {
          // SSE streaming done event — capture citations and freshness in refs for message finalization
          if (event.citations) {
            citationsRef.current = event.citations;
            dataFreshnessRef.current = event.dataFreshness;
          }
          addTraceStep({ type: 'results', label: 'Complete' });
          updateAgentState({ status: 'complete', progress: 100, thinking: null });
        } else if (event.type === 'thinking') {
          if (event.status === 'active') {
            addTraceStep({
              type: event.step?.includes('tool') ? 'tool_call' : 'thinking',
              label: event.label || 'Processing',
              streaming: true,
            });
          } else {
            // Update the most recent active thinking step to mark it complete
            setTraceSteps(prev => {
              const lastActiveIdx = prev.map((s, i) => ({ s, i })).filter(({ s }) => s.streaming && s.type === 'thinking').pop();
              if (lastActiveIdx) {
                return prev.map((s, i) => i === lastActiveIdx.i
                  ? { ...s, streaming: false, label: event.label || s.label, duration: event.duration_ms }
                  : s);
              }
              return prev;
            });
          }
        } else if (event.type === 'tool_result') {
          // Handle tool_result from agentChat.js (named SSE events)
          // Update the most recent streaming tool call step
          setTraceSteps(prev => {
            const toolStep = [...prev].reverse().find(s => s.type === 'tool_call' && s.streaming);
            if (toolStep) {
              return prev.map(s =>
                s.id === toolStep.id
                  ? { ...s, streaming: false, status: event.success ? 'success' : 'error' }
                  : s
              );
            }
            return prev;
          });
          // Extract MCP App artifact if present
          if (event.mcpApp) {
            addArtifact({
              type: ArtifactType.MCP_APP,
              title: event.mcpApp.title || 'Visualization',
              resourceUri: event.mcpApp.uri,
              toolName: event.tool,
              toolInput: {},
              toolResult: event.toolResult || {},
            });
          }
        } else if (event.type === 'tool_call') {
          if (event.status === 'started') {
            addTraceStep({ type: 'tool_call', label: event.label || event.name, streaming: true });
          } else {
            setTraceSteps(prev => {
              const step = [...prev].reverse().find(s => s.type === 'tool_call' && s.streaming);
              if (step) {
                return prev.map(s => s.id === step.id
                  ? { ...s, streaming: false, duration: event.duration_ms, rows: event.rows, source: event.source }
                  : s
                );
              }
              return prev;
            });
          }
        }
    }
  }, [addTraceStep, updateAgentState, addArtifact, getAgentStatus, calculateProgress, agentState.query]);

  /**
   * Send a message to the agent
   */
  const sendMessage = useCallback(async (message, options = {}) => {
    // Clear previous trace and artifacts for fresh response
    clearTrace();
    setArtifacts([]);
    setIsLoading(true);
    setError(null);

    // Initialize agent state
    updateAgentState({
      status: 'analyzing',
      query: message,
      progress: 5,
      currentAgent: 'statcast-orchestrator',
      thinking: 'Analyzing your question...',
    });

    // Add user message
    const userMessage = { role: 'user', content: message };
    setMessages(prev => [...prev, userMessage]);

    // Fire onTurnComplete for user message
    onTurnCompleteRef.current?.({
      role: 'user',
      content: message,
      timestamp: Date.now(),
    });

    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      // Build conversation history from current messages ref (avoids stale closure)
      const conversationHistory = messagesRef.current.map(msg => ({
        role: msg.role,
        content: msg.content,
      }));

      // Extract video URI from uploaded files if present
      const files = options.properties?.files || [];
      const videoFile = files.find(f => f.mimeType?.startsWith('video/'));
      const videoUri = options.properties?.videoUri || videoFile?.gsUri || videoFile?.uri || null;

      const requestEndpoint = options.endpointOverride || endpoint;
      const requestContext = options.contextPayload || {};
      const contextForRequest = {
        sport: requestContext.sport || requestContext.sportCode || sport,
        sportCategory: requestContext.sportCategory || requestContext.sportType || sport,
        sportType: requestContext.sportType || requestContext.sportCategory || sport,
        sportCode: requestContext.sportCode || requestContext.leagueCode || null,
        leagueCode: requestContext.leagueCode || requestContext.league || null,
        leagueName: requestContext.leagueName || null,
        leagueLevel: requestContext.leagueLevel || null,
        gender: requestContext.gender || null,
        teamId: requestContext.teamId || null,
        teamName: requestContext.teamName || requestContext.team || null,
        teamAbbrev: requestContext.teamAbbrev || null,
        conference: requestContext.conference || null,
      };

      const response = await fetch(requestEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': options.sessionId || `session-${Date.now()}`,
        },
        body: JSON.stringify({
          message,
          query: message,
          threadId: options.threadId || options.sessionId,
          // Include conversation history for context
          history: conversationHistory,
          ...contextForRequest,
          // Language preference for multilingual responses
          language: options.language || undefined,
          sessionId: options.sessionId,
          agentId: options.agentId || `${sport}_analyst`,
          // Video URI for orchestrator routing
          videoUri,
          // All files for multi-modal processing
          files,
          // Model selection override (e.g. 'claude-opus')
          model: options.properties?.model || undefined,
          properties: {
            sport,
            sportType: sport,
            isBaseball: sport === 'baseball',
            ...contextForRequest,
            ...options.properties,
          },
          options: {
            includeCharts: options.includeCharts !== false,
            includeStrategy: options.includeStrategy !== false,
            ...options.agentOptions,
          },
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const contentType = response.headers.get('content-type') || '';

      // Handle JSON response (from statcast and similar endpoints)
      if (contentType.includes('application/json')) {
        const data = await response.json();

        // Ask SLAM AI guard — the server rejected the input before opening
        // the SSE stream. Render the refusal as a normal assistant turn.
        if (data?.guardRefused) {
          const refusalText = data.message || 'I can only help with sports questions.';
          textBufferRef.current = refusalText;
          setTextContent(refusalText);
          updateAgentState({ status: 'complete', progress: 100, thinking: null });
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: refusalText,
            guardRefused: true,
            agentState: { status: 'complete', progress: 100 },
          }]);
          onTurnCompleteRef.current?.({
            role: 'assistant',
            content: refusalText,
            guardRefused: true,
            timestamp: Date.now(),
          });
          return;
        }

        // Unwrap respond() helper wrapper: {success, data: {...}} pattern used by all backend routes
        const unwrappedData = (data?.success !== undefined && data?.data && typeof data.data === 'object')
          ? data.data : data;

        if (isBasketballNl2SqlResponse(unwrappedData)) {
          const responseText = buildNl2SqlAssistantMessage(message, unwrappedData);
          const rows = unwrappedData.results || [];
          const columns = Object.keys(rows[0] || {});
          const serverWidgets = Array.isArray(unwrappedData.widgets) ? unwrappedData.widgets : [];

          setTraceSteps(prev => prev.map((step, idx) =>
            idx === 0 ? { ...step, streaming: false, label: 'Query analyzed' } : step
          ));

          addTraceStep({
            type: 'sql',
            label: 'SQL generated',
            text: unwrappedData.sql || null,
          });

          if (rows.length > 0) {
            addTraceStep({
              type: 'results',
              label: `Fetched ${unwrappedData.rowCount || rows.length} rows`,
            });
          }

          textBufferRef.current = responseText;
          setTextContent(responseText);

          if (unwrappedData.sql) {
            addArtifact({
              type: ArtifactType.SQL_QUERY,
              title: 'Generated SQL',
              data: {
                generatedSQL: unwrappedData.sql,
                naturalLanguage: message,
                status: 'executed',
                results: rows,
              },
            });
          }

          if (rows.length > 0) {
            addArtifact({
              type: ArtifactType.DATA_TABLE,
              title: 'BigQuery Results',
              data: {
                title: 'BigQuery Results',
                summary: `${unwrappedData.rowCount || rows.length} rows from basketball analysis`,
                rows,
                columns,
              },
            });

            // MCP App: interactive data explorer for tabular results
            addArtifact({
              type: ArtifactType.MCP_APP,
              title: 'Interactive Data Explorer',
              resourceUri: 'ui://slam/data-explorer',
              toolName: 'query_basketball_data',
              toolInput: { query: message },
              toolResult: { rows, columns, title: 'Query Results' },
            });

            // MCP App: shot chart if data has coordinate fields
            if (rows.length > 0 && rows[0] && ('x' in rows[0] || 'loc_x' in rows[0])) {
              addArtifact({
                type: ArtifactType.MCP_APP,
                title: 'Shot Chart',
                resourceUri: 'ui://slam/shot-chart',
                toolName: 'query_basketball_data',
                toolInput: { query: message },
                toolResult: { shots: rows, player: unwrappedData.player || '' },
              });
            }

            const serverChartWidgets = serverWidgets.filter(
              (widget) => widget?.widgetType === 'chart' && widget?.data
            );
            if (serverChartWidgets.length > 0) {
              serverChartWidgets.forEach((widget) => {
                addArtifact({
                  type: ArtifactType.CHART,
                  title: widget?.data?.config?.title || widget?.data?.title || 'Visualization',
                  data: widget.data,
                });
              });
            }

            const inferredChart = inferVisualizationFromQuery({
              query: message,
              results: rows,
              intent: unwrappedData.entities?.intent || null,
            });

            if (inferredChart && serverChartWidgets.length === 0) {
              addArtifact({
                type: ArtifactType.CHART,
                title: inferredChart.config?.title || 'Visualization',
                data: inferredChart,
              });
            }
          }

          addTraceStep({
            type: 'results',
            label: 'Complete',
          });

          updateAgentState({ status: 'complete', progress: 100, thinking: null });
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: responseText,
            sources: ['BigQuery'],
            model: 'basketball-nl2sql',
            agentState: { status: 'complete', progress: 100 },
          }]);

          // Fire onTurnComplete for NL2SQL assistant response
          onTurnCompleteRef.current?.({
            role: 'assistant',
            content: responseText,
            agentId: 'basketball-nl2sql',
            artifacts: [...artifactsRef.current],
            timestamp: Date.now(),
          });
          return;
        }

        // Update initial trace step to show querying
        setTraceSteps(prev => prev.map((step, idx) =>
          idx === 0 ? { ...step, streaming: false, label: 'Query analyzed' } : step
        ));

        // Add querying step
        addTraceStep({
          type: 'sql',
          label: 'Querying database',
          streaming: false,
        });

        // Unwrap respond() helper wrapper: {success, data: {...}} pattern used by basketball routes
        const payload = (data.success !== undefined && data.data && typeof data.data === 'object') ? data.data : data;
        // Extract response text
        const responseText = payload.response || payload.message || payload.text || data.response || data.message || data.text || '';
        textBufferRef.current = responseText;
        setTextContent(responseText);

        // Add trace steps from agent trace if available
        if (data.agentTrace && data.agentTrace.length > 0) {
          data.agentTrace.forEach((trace, idx) => {
            addTraceStep({
              type: trace.type || 'thinking',
              label: trace.label || trace.text || `Step ${idx + 1}`,
              text: trace.text,
              thought: trace.thought,
              content: trace.text, // For display in StepDetail
              duration: trace.duration,
            });
          });
        }

        if (data.orchestration?.applied) {
          addTraceStep({
            type: 'reasoning',
            label: 'Claude orchestration',
            thought: data.orchestration.summary || data.orchestration.intent || 'Query orchestration applied',
          });
        }

        // Add completion step
        addTraceStep({
          type: 'results',
          label: 'Complete',
        });

        // Update agent state to complete
        updateAgentState({ status: 'complete', progress: 100, thinking: null });

        // Handle chart specs as artifacts
        if (data.chartSpecs && data.chartSpecs.length > 0) {
          data.chartSpecs.forEach(spec => {
            addArtifact({
              type: ArtifactType.CHART,
              title: spec.title || 'Chart',
              data: spec,
            });
          });
        }

        // Handle Shiny specs as interactive visualization artifacts
        // Includes story (data narrative) and queryInfo (SQL provenance)
        if (data.shinySpecs && data.shinySpecs.length > 0) {
          data.shinySpecs.forEach(spec => {
            addArtifact({
              type: ArtifactType.SHINY,
              title: spec.title || 'Shiny Visualization',
              data: {
                ...spec,
                // Ensure query info is included for source attribution
                queryInfo: spec.queryInfo || {
                  sql: data.statcastData?.query,
                  source: data.sources?.primary || ['Statcast'],
                  explanation: data.statcastData?.explanation,
                },
              },
            });
          });
        }

        // Handle statcast data as data table artifact + MCP App
        if (data.statcastData && data.statcastData.data && data.statcastData.data.length > 0) {
          const statcastRows = data.statcastData.data.slice(0, 100);
          const statcastColumns = Object.keys(data.statcastData.data[0] || {});
          addArtifact({
            type: ArtifactType.DATA_TABLE,
            title: 'Statcast Data',
            data: {
              rows: statcastRows,
              columns: statcastColumns,
            },
          });

          // MCP App: interactive data explorer for statcast results
          addArtifact({
            type: ArtifactType.MCP_APP,
            title: 'Statcast Explorer',
            resourceUri: 'ui://slam/data-explorer',
            toolName: 'query_statcast',
            toolInput: { query: message },
            toolResult: { rows: statcastRows, columns: statcastColumns, title: 'Statcast Data' },
          });
        }

        // Handle video analysis artifact (from orchestrator)
        if (data.videoArtifact) {
          addArtifact({
            type: ArtifactType.VIDEO_REPORT,
            title: data.videoArtifact.title || 'Video Analysis Report',
            data: data.videoArtifact,
          });

          // MCP App: interactive video report
          addArtifact({
            type: ArtifactType.MCP_APP,
            title: data.videoArtifact.title || 'Video Analysis Report',
            resourceUri: 'ui://slam/video-report',
            toolName: 'analyze_video',
            toolInput: {},
            toolResult: data.videoArtifact,
          });
        }

        // Create assistant message with trace steps attached
        // We need to capture current trace steps at this point
        setMessages(prev => {
          // Get the final trace steps that will be attached to this message
          return [...prev, {
            role: 'assistant',
            content: responseText,
            sources: payload.sources || data.sources,
            citations: payload.citations || data.citations,
            dataFreshness: payload.dataFreshness || data.dataFreshness,
            toolCalls: payload.toolCalls || data.toolCalls,
            model: payload.model || data.model,
            agentState: { status: 'complete', progress: 100 },
          }];
        });

        // Fire onTurnComplete for JSON assistant response
        onTurnCompleteRef.current?.({
          role: 'assistant',
          content: responseText,
          agentId: payload.agentId || data.agentId,
          toolCalls: payload.toolCalls || data.toolCalls,
          artifacts: [...artifactsRef.current],
          timestamp: Date.now(),
        });

        return;
      }

      // Handle SSE stream (for AG-UI compatible endpoints)
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              handleEvent(event);
            } catch (e) {
              // Handle non-JSON data events
              if (line.slice(6).trim()) {
                textBufferRef.current += line.slice(6);
                setTextContent(textBufferRef.current);
              }
            }
          }
        }
      }

      // Finalize assistant message (use refs to avoid stale closures)
      if (textBufferRef.current) {
        const finalContent = textBufferRef.current;
        // If the post-filter tripped, drop artifacts/widgets/traces — we
        // don't want to expose partial analysis that came with a leak.
        const refused = guardRefusedRef.current;
        const finalArtifacts = refused ? [] : [...artifactsRef.current];
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: finalContent,
          widgets: refused ? [] : [...widgetsRef.current],
          traceSteps: refused ? [] : [...traceStepsRef.current],
          artifacts: finalArtifacts,
          citations: refused ? undefined : (citationsRef.current || undefined),
          dataFreshness: refused ? undefined : (dataFreshnessRef.current || undefined),
          guardRefused: refused || undefined,
        }]);

        // Fire onTurnComplete for SSE assistant response
        onTurnCompleteRef.current?.({
          role: 'assistant',
          content: finalContent,
          artifacts: finalArtifacts,
          guardRefused: refused || undefined,
          timestamp: Date.now(),
        });
      }

    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('[useAgentChat] Request aborted');
      } else {
        console.error('[useAgentChat] Error:', err);
        setError(err.message || 'Failed to process message');
        updateAgentState({ status: 'error' });
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    endpoint,
    sport,
    handleEvent,
    clearTrace,
    updateAgentState,
    addTraceStep,
    addArtifact,
  ]);

  /**
   * Cancel the current request
   */
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsLoading(false);
    updateAgentState({ status: 'idle' });
  }, [updateAgentState]);

  /**
   * Pin/unpin an artifact
   */
  const toggleArtifactPin = useCallback((artifactId) => {
    setArtifacts(prev => prev.map(a =>
      a.id === artifactId ? { ...a, pinned: !a.pinned } : a
    ));
  }, []);

  /**
   * Remove an artifact
   */
  const removeArtifact = useCallback((artifactId) => {
    setArtifacts(prev => prev.filter(a => a.id !== artifactId));
  }, []);

  /**
   * Computed status message
   */
  const statusMessage = useMemo(() => {
    const statusMessages = {
      idle: 'Ready',
      analyzing: 'Analyzing your question...',
      routing: 'Routing to specialist...',
      querying: 'Querying database...',
      interpreting: 'Interpreting data...',
      strategizing: 'Generating recommendations...',
      complete: 'Complete',
      error: 'Error occurred',
    };
    return statusMessages[agentState.status] || 'Processing...';
  }, [agentState.status]);

  /**
   * Current agent display name
   */
  const currentAgentName = useMemo(() => {
    const agentNames = {
      'statcast-orchestrator': 'Orchestrator',
      'statcast-nl2sql': 'Data Agent',
      'statcast-insight': 'Insight Agent',
      'statcast-strategy': 'Strategy Agent',
      'baseball_analyst': 'Baseball Analyst',
      'routing_agent': 'Router',
    };
    return agentNames[agentState.currentAgent] || 'Agent';
  }, [agentState.currentAgent]);

  return {
    // Actions
    sendMessage,
    cancel,
    reset,
    clearTrace,
    toggleArtifactPin,
    removeArtifact,
    setMessages,

    // State
    isLoading,
    error,
    messages,
    textContent,

    // Agent State
    agentState,
    statusMessage,
    currentAgentName,

    // Trace & Visualization
    traceSteps,
    widgets,
    artifacts,
  };
}

export default useAgentChat;
